import { FormikProps } from "formik";
import React, { useMemo } from "react";
import Button from "../../Button";
import Input from "../../Input";
import PhoneInput from "../../PhoneInput";
import { MainFormType } from "../CreateServiceModal";
import DatePicker from "../../DatePicker";

interface PropTypes {
  form: FormikProps<MainFormType>;
  onSubmit: () => void;
  isLoading?: boolean
}

const OrderInformation = ({ form, onSubmit, isLoading }: PropTypes) => {
  const orderInfo = form.values.package_pickup_meta.orderInformation;

  const isBusinessUser = form.values.user?.selectedAccountType === "business";

  const errors = useMemo(() => {
    if (!form.errors.package_pickup_meta?.orderInformation) return {};
    return form?.errors?.package_pickup_meta?.orderInformation;
  }, [form.errors.package_pickup_meta?.orderInformation]);

  const touched = form.touched.package_pickup_meta?.orderInformation || {};

  // Helper function to check if two dates are the same day
  const isSameDay = (date1: Date, date2: Date) => {
    return date1.toDateString() === date2.toDateString();
  };

  // Calculate minTime based on selected date
  const getMinTime = () => {
    const selectedDate = form.values.scheduleTime;
    const today = new Date();

    if (!selectedDate || isSameDay(selectedDate, today)) {
      // If no date selected or today is selected, use current time as minimum
      return new Date();
    } else {
      // If future date is selected, allow selection from start of day (00:00)
      return new Date(new Date().setHours(0, 0, 0, 0));
    }
  };

  // Handle date/time change with validation
  const handleDateTimeChange = (date: Date | null) => {
    if (!date) {
      form.setFieldValue("scheduleTime", null);
      return;
    }

    const today = new Date();
    const minTime = isSameDay(date, today)
      ? new Date()
      : new Date(new Date().setHours(0, 0, 0, 0));

    // If switching to today and current selected time is before minTime, update to minTime
    if (isSameDay(date, today) && date < minTime) {
      // Set the date to current time (which is the minimum allowed)
      form.setFieldValue("scheduleTime", new Date());
    } else {
      form.setFieldValue("scheduleTime", date);
    }
  };

  const handleFieldChange = (field: string, value: string) => {
    form.setFieldValue(`package_pickup_meta.orderInformation.${field}`, value);
  };

  const handleFieldBlur = (field: string) => {
    form.setFieldTouched(`package_pickup_meta.orderInformation.${field}`, true);
  };

  console.log(errors, "errors.orderinformation");

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="flex flex-col gap-4"
    >
      <div className="relative">
        <p className="text-lg font-semibold text-center">Order information</p>
        <div className="absolute top-1/2 md:-translate-y-1/2 translate-y-[40%]  right-0">
          <Button
            type="button"
            onClick={() => {
              if (form.values?.user) {
                const userNameArr = form.values.user?.fullName?.split(" ");
                if (userNameArr.length > 0) {
                  handleFieldChange("firstName", userNameArr[0]);
                }
                if (userNameArr.length > 1) {
                  handleFieldChange("lastName", userNameArr[1]);
                }
                handleFieldChange("phone", form.values.user.phone);
              }
            }}
            className="w-full md:w-32 min-h-0 h-10 md:h-10 px-2"
          >
            Autofill
          </Button>
        </div>
      </div>
      <div className="md:grid gap-4  md:grid-cols-2 flex flex-col">
        <Input
          onChange={(e) => handleFieldChange("firstName", e.target.value)}
          error={errors.firstName}
          onBlur={() => handleFieldBlur("firstName")}
          value={orderInfo.firstName}
          name="firstName"
          isInvalid={Boolean(touched.firstName && errors.firstName)}
          label="First name *"
          placeholder="Enter first name"
          className='text-[1rem] lg:text-xs'
          labelClassName='text-[1rem] lg:text-xs'
        />
        <Input
          onChange={(e) => handleFieldChange("lastName", e.target.value)}
          error={errors.lastName}
          onBlur={() => handleFieldBlur("lastName")}
          value={orderInfo.lastName}
          name="lastName"
          isInvalid={Boolean(touched.lastName && errors.lastName)}
          label="Last name *"
          className='text-[1rem] lg:text-xs'
          labelClassName='text-[1rem] lg:text-xs'
          placeholder="Enter last name"
        />
        <PhoneInput
          onChange={(e) => handleFieldChange("phone", e)}
          error={errors.phone}
          onBlur={() => handleFieldBlur("phone")}
          value={orderInfo.phone}
          name="phone"
          isInvalid={Boolean(touched.phone && errors.phone)}
          label="Phone *"
          placeholder="Enter phone number"
          labelClassName='text-[1rem] lg:text-xs'
          responsiveFont={true}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              onSubmit();
            }
          }}
        />
        {isBusinessUser && (
          <DatePicker
            showTimeSelect
            placeholderText="Pickup Now"
            selected={form.values.scheduleTime}
            minDate={new Date()} // Disables past dates
            minTime={getMinTime()} // Conditionally set minimum time
            maxTime={new Date(new Date().setHours(23, 59, 59, 999))} // Set to end of day
            label="Schedule time"
            popperPlacement="top"
            dateFormat="dd/MM/yyyy HH:mm"
            onChange={handleDateTimeChange}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                onSubmit();
              }
            }}
          />
        )}
      </div>
      <div className='flex justify-center'>
        <Button className="w-full md:w-32 min-h-0 h-10 md:h-10 mt-4" isLoading={isLoading}>Continue</Button>
      </div>
    </form>
  );
};

export default OrderInformation;
